import type * as Api from 'Api'
/**
 * Возвращает ошибку из переданного справочника ошибок
 * через метод createError
 *
 * Пример использования: в экзепляре конкретного httpService
 * создается экземпляр сервиса, в который передается справочник ошибок.
 * ошибка обрабатывается в catch блоке метода через createError
 * @example
 * ```typescript
 * const httpErrorService = new HttpErrorService(<справочник_ошибок>)
 *
 * await asyncFunc() {
 *   ...smth
 * }.catch((error) => {
 *   throw this.httpErrorService.createError(error, errorCategoryName)
 * })
 * ```
 *
 * @returns
 * ```typescript
 * type Api.ThrowingError
 * {
 *   url - request url
 *   store - store name
 *   code - request code
 *   message - custom message
 *   status - request status
 * }
 * ```
 */

export class HttpErrorService<K extends string> {
  private readonly errorsList: Api.ErrorsHandbook<K>
  private readonly namespace: string
  private readonly defaultError: Api.ErrorResponseDataError = {
    message: 'Что-то пошло не так',
  }

  /**
   *
   * @param errorsList - Справочник ошибок
   * @param namespace - Имя сервиса, в котором ошибки выбрасываются
   */

  constructor(errorsList: Api.ErrorsHandbook<K>, namespace: string) {
    this.errorsList = errorsList
    this.namespace = namespace
  }

  /**
   * возвращает объект ошибки из справочника
   * @param url
   * @param error - ошибка от бэкенда
   * @param errorCategoryName optional -  ключ категории для оптимизации поиска
   * @returns мутрированная ошибка для интерфейса
   */

  public getThrowingError(
    error: Api.ErrorServerResponse,
    url?: string,
    errorCategoryName?: K,
  ): Api.ThrowingError {
    const httpStatus = error.response?.status || error.status

    const foundError: Api.ErrorResponseDataError = errorCategoryName
      ? this.searchErrorInHandbookCategory(httpStatus, errorCategoryName)
      : this.searchErrorInHandbook(httpStatus)

    const throwingError: Api.ThrowingError = {
      code: error?.response?._data?.error?.code,
      httpStatus,
      message: foundError.message,
      url,
      namespace: this.namespace,
      error: this.mapError(error),
      detail: error?.response?._data?.detail,
    }

    // if (process.env.NODE_ENV === 'development') console.log(throwingError)
    console.log(throwingError)

    return throwingError
  }

  /**
   * корректирует поля ошибки для соответсвия ожидаемым интерфейсам Api.ThrowingError
   * @param error
   * @private
   */
  private mapError(error: Api.ErrorServerResponse) {
    if (error.response) {
      return process.client
        ? {
            ...error,
            response: {
              ...error.response,
              data: error.response._data,
            },
          }
        : StringifyError(error)
    }

    return StringifyError(error)
  }

  /**
   * ищет ошибку во всем категориям
   * @param httpStatus
   * @private
   * @returns error default || found error найденная в справочнике ошибка
   */

  private searchErrorInHandbook(
    httpStatus: number,
  ): Api.ErrorResponseDataError {
    let foundError: Api.ErrorResponseDataError | undefined

    for (const errorCategoryKey in this.errorsList) {
      const errorCategory = this.errorsList[errorCategoryKey]

      foundError = this.searchInErrorCategoryOfHandbook(
        httpStatus,
        errorCategory,
      )

      if (foundError) break
    }

    return foundError || this.defaultError
  }

  /**
   * ищет ошибку в конкретной категории справочника
   * @param httpStatus
   * @param errorCategoryName
   * @private
   * @returns error default || found error найденная в конкретной категории ошибка
   */

  private searchErrorInHandbookCategory(
    httpStatus: number,
    errorCategoryName: keyof typeof this.errorsList,
  ): Api.ErrorResponseDataError {
    const errorCategory = this.errorsList[errorCategoryName]

    return (
      this.searchInErrorCategoryOfHandbook(httpStatus, errorCategory) ||
      this.defaultError
    )
  }

  /**
   * ищет ошибку по совпадению статуса и кода
   * @param httpStatus
   * @param errorCategory
   * @private
   * @returns error || undefined найденная в категории ошибка
   */

  private searchInErrorCategoryOfHandbook(
    httpStatus: number,
    errorCategory: Api.ErrorResponseDataError[],
  ): Api.ErrorResponseDataError | undefined {
    return errorCategory.find((errorItem) => {
      return errorItem.code === httpStatus
    })
  }
}
